/**
 * 🧠 Memory Snapshot Component - Redesigned
 * 
 * Shows what Allora remembers in the EXACT structure users expect:
 * 
 * Structure (collapsed by default):
 * - Emotional patterns (3-5 items max)
 * - Work rhythms (if applicable)
 * - Health signals (only if user uses them)
 * - User-defined truths ("I hate mornings", "I like direct answers")
 * 
 * Each item:
 * - Plain English explanation
 * - Editable
 * - Deletable (❌ Forget button)
 * 
 * User feeling: "I am in control."
 */
import React, { useState, useEffect } from 'react';
import { 
  Brain, 
  Heart, 
  Briefcase, 
  Activity, 
  User,
  ChevronDown, 
  ChevronRight,
  Trash2, 
  Edit3,
  X,
  Check,
  Loader2 
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// The 4 categories as specified
const MEMORY_CATEGORIES = {
  emotional_patterns: {
    key: 'emotional_patterns',
    title: 'Emotional patterns',
    icon: Heart,
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-50',
    description: 'How you tend to feel'
  },
  work_rhythms: {
    key: 'work_rhythms',
    title: 'Work rhythms',
    icon: Briefcase,
    color: 'text-blue-500',
    bgColor: 'bg-blue-50',
    description: 'Your productivity patterns'
  },
  health_signals: {
    key: 'health_signals',
    title: 'Health signals',
    icon: Activity,
    color: 'text-green-500',
    bgColor: 'bg-green-50',
    description: 'Patterns in your wellbeing'
  },
  user_truths: {
    key: 'user_truths',
    title: 'Things you\'ve told me',
    icon: User,
    color: 'text-teal-500',
    bgColor: 'bg-teal-50',
    description: 'Your preferences and facts'
  }
};

const MemorySnapshotRedesigned = ({ onClose }) => {
  const [memories, setMemories] = useState({
    emotional_patterns: [],
    work_rhythms: [],
    health_signals: [],
    user_truths: []
  });
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({}); // All collapsed by default
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchMemories();
  }, []);

  const fetchMemories = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/allora/memory-snapshot`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        // Transform API response to our category structure
        const categorized = categorizeMemories(data.snapshot?.categories || {});
        setMemories(categorized);
      }
    } catch (error) {
      console.error('Error fetching memories:', error);
    } finally {
      setLoading(false);
    }
  };

  // Transform backend categories to our 4-category structure
  const categorizeMemories = (apiCategories) => {
    const result = {
      emotional_patterns: [],
      work_rhythms: [],
      health_signals: [],
      user_truths: []
    };

    // Map from API categories to our structure
    Object.entries(apiCategories).forEach(([key, category]) => {
      const mems = category.memories || [];
      
      mems.forEach(mem => {
        const item = {
          id: mem.id,
          content: mem.content,
          plainEnglish: toPlainEnglish(mem.content, key),
          confidence: mem.confidence || 0.7,
          createdAt: mem.created_at
        };

        // Route to appropriate category
        if (key === 'emotions' || key === 'mood' || mem.content?.toLowerCase().includes('feel')) {
          result.emotional_patterns.push(item);
        } else if (key === 'work' || key === 'productivity' || mem.content?.toLowerCase().includes('work')) {
          result.work_rhythms.push(item);
        } else if (key === 'health' || mem.content?.toLowerCase().includes('health') || mem.content?.toLowerCase().includes('sleep')) {
          result.health_signals.push(item);
        } else {
          result.user_truths.push(item);
        }
      });
    });

    // Limit emotional patterns to 5 items
    result.emotional_patterns = result.emotional_patterns.slice(0, 5);

    return result;
  };

  // Convert memory content to plain English
  const toPlainEnglish = (content, category) => {
    // Already human-readable, return as is
    if (content && content.includes(' ')) {
      return content;
    }
    return content;
  };

  const toggleCategory = (key) => {
    setExpandedCategories(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const startEdit = (memory) => {
    setEditingId(memory.id);
    setEditText(memory.plainEnglish || memory.content);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const saveEdit = async (memory) => {
    try {
      const response = await fetch(`${API_URL}/api/allora/memories/${memory.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: editText })
      });

      if (response.ok) {
        toast.success('Memory updated');
        fetchMemories();
      }
    } catch (error) {
      toast.error('Failed to update');
    }
    setEditingId(null);
    setEditText('');
  };

  const deleteMemory = async (memoryId) => {
    try {
      setDeletingId(memoryId);
      const response = await fetch(`${API_URL}/api/allora/memories/${memoryId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Forgotten');
        fetchMemories();
      }
    } catch (error) {
      toast.error('Failed to forget');
    } finally {
      setDeletingId(null);
    }
  };

  const totalMemories = Object.values(memories).reduce((sum, arr) => sum + arr.length, 0);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-lg max-w-lg mx-auto">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-teal-500" />
          <span className="ml-2 text-gray-500">Loading memories...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg max-w-lg mx-auto overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-500 to-cyan-500 p-5 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-semibold">What I remember about you</h2>
              <p className="text-white/70 text-sm">{totalMemories} memories</p>
            </div>
          </div>
          {onClose && (
            <button 
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Categories - ALL COLLAPSED BY DEFAULT */}
      <div className="p-4 space-y-2">
        {totalMemories === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Brain className="w-8 h-8 text-teal-400" />
            </div>
            <p className="text-gray-500 mb-1">No memories yet</p>
            <p className="text-gray-400 text-sm">I'll learn about you as we talk</p>
          </div>
        ) : (
          Object.entries(MEMORY_CATEGORIES).map(([key, category]) => {
            const categoryMemories = memories[key] || [];
            if (categoryMemories.length === 0) return null;

            const isExpanded = expandedCategories[key];
            const Icon = category.icon;

            return (
              <div key={key} className="border border-gray-100 rounded-xl overflow-hidden">
                {/* Category Header - Click to expand */}
                <button
                  onClick={() => toggleCategory(key)}
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 ${category.bgColor} rounded-lg flex items-center justify-center`}>
                      <Icon className={`w-4 h-4 ${category.color}`} />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-gray-800 text-sm">{category.title}</p>
                      <p className="text-xs text-gray-400">{categoryMemories.length} items</p>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  )}
                </button>

                {/* Memory Items - Shown when expanded */}
                {isExpanded && (
                  <div className="border-t border-gray-100 p-3 space-y-2 bg-gray-50/50">
                    {categoryMemories.map((memory) => (
                      <div 
                        key={memory.id}
                        className="bg-white rounded-lg p-3 text-sm group"
                      >
                        {editingId === memory.id ? (
                          // Edit mode
                          <div className="space-y-2">
                            <textarea
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              className="w-full p-2 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:border-teal-400"
                              rows={2}
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => saveEdit(memory)}
                                className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs hover:bg-green-200"
                              >
                                <Check className="w-3 h-3" />
                                Save
                              </button>
                              <button
                                onClick={cancelEdit}
                                className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          // View mode
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-gray-700 leading-relaxed">
                              {memory.plainEnglish || memory.content}
                            </p>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => startEdit(memory)}
                                className="p-1.5 text-gray-400 hover:text-teal-500 hover:bg-teal-50 rounded transition-colors"
                                title="Edit"
                              >
                                <Edit3 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => deleteMemory(memory.id)}
                                disabled={deletingId === memory.id}
                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                title="Forget"
                              >
                                {deletingId === memory.id ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Trash2 className="w-3.5 h-3.5" />
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer - Control message */}
      {totalMemories > 0 && (
        <div className="px-4 pb-4">
          <p className="text-xs text-center text-gray-400">
            You can edit or delete any memory. You're in control.
          </p>
        </div>
      )}
    </div>
  );
};

export default MemorySnapshotRedesigned;
